import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Users, Gift, LogOut, Plus, Search, X, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCustomers({ user, setUser }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [cashbackAmount, setCashbackAmount] = useState(0);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [search, setSearch] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const navigate = useNavigate();

  // Filter customers by name, email, phone, city, address, postal code
  const normalized = search.trim().toLowerCase();
  const filteredCustomers = normalized
    ? customers.filter((c) => {
        const fields = [
          c.name,
          c.last_name,
          c.email,
          c.phone,
          c.nif,
          c.address,
          c.city,
          c.province,
          c.postal_code,
        ];
        return fields.some(
          (f) => f && String(f).toLowerCase().includes(normalized)
        );
      })
    : customers;

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchCustomers();
  }, [user, navigate]);

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/customers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setCustomers(data.customers || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerDetail = async (customerId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/customers/${customerId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSelectedCustomer(data);
      setCashbackAmount(data.cashback_balance || 0);
      setEditMode(false);
    } catch (error) {
      toast.error('Error al cargar detalles del cliente');
    }
  };

  const startEdit = () => {
    if (!selectedCustomer) return;
    setEditForm({
      first_name: selectedCustomer.first_name || '',
      last_name: selectedCustomer.last_name || '',
      name: selectedCustomer.name || '',
      email: selectedCustomer.email || '',
      phone: selectedCustomer.phone || '',
      nif: selectedCustomer.nif || '',
      address: selectedCustomer.address || '',
      city: selectedCustomer.city || '',
      province: selectedCustomer.province || '',
      postal_code: selectedCustomer.postal_code || '',
      country: selectedCustomer.country || 'España',
    });
    setEditMode(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!selectedCustomer) return;
    const payload = { ...editForm };
    // If first/last name are filled, let backend recompute display name; otherwise keep `name`
    if (payload.first_name || payload.last_name) {
      delete payload.name;
    }
    setSavingEdit(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/admin/customers/${selectedCustomer.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || 'Error al guardar');
        return;
      }
      toast.success('Datos del cliente actualizados');
      setEditMode(false);
      fetchCustomers();
      fetchCustomerDetail(selectedCustomer.id);
    } catch {
      toast.error('Error de conexión');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleUpdateCashback = async () => {
    if (!selectedCustomer) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/customers/${selectedCustomer.id}/cashback`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ balance: parseFloat(cashbackAmount) })
      });
      
      if (response.ok) {
        toast.success('Cashback actualizado');
        fetchCustomers();
        fetchCustomerDetail(selectedCustomer.id);
      }
    } catch (error) {
      toast.error('Error al actualizar cashback');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center">Cargando...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-black text-white">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }}>ADMIN - ARTHUR FITZ</h1>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white hover:underline">Ver Tienda</Link>
            <Button onClick={handleLogout} variant="outline" className="text-white border-white">
              <LogOut className="w-4 h-4 mr-2" /> Cerrar Sesión
            </Button>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b">
        <div className="container mx-auto px-4 sm:px-6 py-3 overflow-x-auto">
          <div className="flex gap-6 whitespace-nowrap">
            <Link to="/admin" className="text-gray-600 hover:text-black">Dashboard</Link>
            <Link to="/admin/products" className="text-gray-600 hover:text-black">Productos</Link>
            <Link to="/admin/orders" className="text-gray-600 hover:text-black">Pedidos</Link>
            <Link to="/admin/customers" className="font-medium text-black border-b-2 border-black pb-2">Clientes</Link>
            <Link to="/admin/coupons" className="text-gray-600 hover:text-black">Cupones</Link>
            <Link to="/admin/quotes" className="text-gray-600 hover:text-black">Presupuestos</Link>
            <Link to="/admin/shipping" className="text-gray-600 hover:text-black">Envios</Link>
            <Link to="/admin/contact-requests" className="text-gray-600 hover:text-black">Solicitudes</Link>
            <Link to="/admin/notices" className="text-gray-600 hover:text-black">Avisos</Link>
            <Link to="/admin/newsletter" className="text-gray-600 hover:text-black">Newsletter</Link>
            <Link to="/admin/gallery" className="text-gray-600 hover:text-black">Galería</Link>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display' }}>Gestión de Clientes</h2>
          <div className="flex items-center gap-2">
            <Button
              onClick={async () => {
                if (!window.confirm('Esto recorrerá todos los pedidos y creará un registro de cliente para cada email que aún no lo tenga. ¿Continuar?')) return;
                try {
                  const token = localStorage.getItem('token');
                  const res = await fetch(`${BACKEND_URL}/api/admin/customers/backfill-from-orders`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                  });
                  const data = await res.json();
                  if (res.ok) {
                    toast.success(`Backfill completado: ${data.examined} pedidos revisados, ${data.linked_to_existing} re-enlazados`);
                    fetchCustomers();
                  } else {
                    toast.error(data.detail || 'Error al ejecutar backfill');
                  }
                } catch {
                  toast.error('Error de conexión');
                }
              }}
              variant="outline"
              className="border-black hover:bg-gray-100"
              data-testid="admin-customers-backfill-btn"
              title="Crea registros de cliente para pedidos antiguos que no tengan uno."
            >
              Sincronizar desde pedidos
            </Button>
            <Button onClick={() => setShowCreateForm(true)} className="bg-black text-white">
              <Plus className="w-4 h-4 mr-2" /> Nuevo Cliente
            </Button>
          </div>
        </div>

        {showCreateForm && (
          <CreateCustomerForm 
            onClose={() => setShowCreateForm(false)}
            onSuccess={() => { setShowCreateForm(false); fetchCustomers(); }}
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {/* Search bar */}
            <div className="bg-white border-2 border-black mb-4 p-3 flex items-center gap-3">
              <Search className="w-5 h-5 text-gray-500 flex-shrink-0" />
              <input
                data-testid="admin-customers-search-input"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nombre, apellido, email, teléfono, dirección, ciudad o CP..."
                className="flex-1 outline-none border-0 bg-transparent text-sm"
              />
              {search && (
                <button
                  data-testid="admin-customers-search-clear"
                  onClick={() => setSearch('')}
                  className="text-gray-400 hover:text-black flex-shrink-0"
                  aria-label="Limpiar búsqueda"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              <span
                data-testid="admin-customers-result-count"
                className="text-xs text-gray-500 flex-shrink-0 ml-2"
              >
                {filteredCustomers.length} de {customers.length}
              </span>
            </div>

            <div className="bg-white border-2 border-black">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px]">
                  <thead>
                    <tr className="border-b-2 border-black">
                      <th className="text-left p-4">Cliente</th>
                      <th className="text-left p-4">Email</th>
                      <th className="text-left p-4">Teléfono</th>
                      <th className="text-left p-4">Cashback</th>
                      <th className="text-left p-4">Registro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCustomers.length === 0 && (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-gray-500 text-sm" data-testid="admin-customers-empty">
                          {customers.length === 0
                            ? 'No hay clientes registrados.'
                            : `No se encontraron clientes que coincidan con "${search}".`}
                        </td>
                      </tr>
                    )}
                    {filteredCustomers.map((customer) => (
                      <tr 
                        key={customer.id} 
                        className="border-b hover:bg-gray-50 cursor-pointer"
                        onClick={() => fetchCustomerDetail(customer.id)}
                      >
                        <td className="p-4 font-medium">{customer.name}</td>
                        <td className="p-4">{customer.email}</td>
                        <td className="p-4">{customer.phone || '-'}</td>
                        <td className="p-4">
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                            {customer.cashback_balance?.toFixed(2) || 0}€
                          </span>
                        </td>
                        <td className="p-4 text-sm">{new Date(customer.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className={selectedCustomer ? "fixed inset-0 z-50 bg-black/60 overflow-y-auto p-3 lg:static lg:bg-transparent lg:p-0 lg:z-auto lg:overflow-visible" : ""}>
            {selectedCustomer ? (
              <div className="bg-white p-6 border-2 border-black lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto" data-testid="admin-customer-detail-panel">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold">Detalle del Cliente</h3>
                  <div className="flex items-center gap-2">
                    {!editMode && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={startEdit}
                        className="border-black hover:bg-gray-100"
                        data-testid="admin-customer-edit-btn"
                      >
                        <Pencil className="w-4 h-4 mr-1" /> Editar
                      </Button>
                    )}
                    <button
                      type="button"
                      onClick={() => { setSelectedCustomer(null); setEditMode(false); }}
                      className="lg:hidden text-3xl leading-none px-3 py-1 -mr-2 hover:bg-gray-100 rounded"
                      aria-label="Cerrar"
                      data-testid="admin-customer-detail-close-mobile"
                    >
                      ×
                    </button>
                  </div>
                </div>
                
                {editMode ? (
                  <form onSubmit={handleSaveEdit} className="space-y-3" data-testid="admin-customer-edit-form">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Nombre</Label>
                        <Input
                          data-testid="edit-first-name"
                          value={editForm.first_name}
                          onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Apellidos</Label>
                        <Input
                          data-testid="edit-last-name"
                          value={editForm.last_name}
                          onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Email *</Label>
                      <Input
                        data-testid="edit-email"
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Teléfono</Label>
                        <Input
                          data-testid="edit-phone"
                          value={editForm.phone}
                          onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">NIF/CIF</Label>
                        <Input
                          data-testid="edit-nif"
                          value={editForm.nif}
                          onChange={(e) => setEditForm({ ...editForm, nif: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Dirección</Label>
                      <Input
                        data-testid="edit-address"
                        value={editForm.address}
                        onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Ciudad</Label>
                        <Input
                          data-testid="edit-city"
                          value={editForm.city}
                          onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Código Postal</Label>
                        <Input
                          data-testid="edit-postal-code"
                          value={editForm.postal_code}
                          onChange={(e) => setEditForm({ ...editForm, postal_code: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Provincia</Label>
                        <Input
                          data-testid="edit-province"
                          value={editForm.province}
                          onChange={(e) => setEditForm({ ...editForm, province: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">País</Label>
                        <select
                          className="w-full border-2 border-gray-300 p-2 rounded text-sm"
                          data-testid="edit-country"
                          value={editForm.country}
                          onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                        >
                          <option value="España">España</option>
                          <option value="Francia">Francia</option>
                          <option value="Portugal">Portugal</option>
                          <option value="Italia">Italia</option>
                          <option value="Alemania">Alemania</option>
                          <option value="Reino Unido">Reino Unido</option>
                          <option value="Estados Unidos">Estados Unidos</option>
                          <option value="México">México</option>
                          <option value="Argentina">Argentina</option>
                          <option value="Otro">Otro</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button
                        type="submit"
                        disabled={savingEdit}
                        className="flex-1 bg-black text-white"
                        data-testid="admin-customer-save-edit"
                      >
                        {savingEdit ? 'Guardando...' : 'Guardar cambios'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setEditMode(false)}
                        data-testid="admin-customer-cancel-edit"
                      >
                        Cancelar
                      </Button>
                    </div>
                  </form>
                ) : (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600">Nombre</p>
                    <p className="font-medium">{selectedCustomer.name}</p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p>{selectedCustomer.email}</p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-600">Teléfono</p>
                    <p>{selectedCustomer.phone || '-'}</p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-600">Dirección</p>
                    <p className="text-sm">{selectedCustomer.address || '-'}</p>
                    <p className="text-sm">{selectedCustomer.postal_code || ''} {selectedCustomer.city || ''}</p>
                    <p className="text-sm">{selectedCustomer.province || ''}</p>
                    <p className="text-sm">{selectedCustomer.country || ''}</p>
                  </div>

                  {selectedCustomer.nif && (
                    <div>
                      <p className="text-sm text-gray-600">NIF/CIF</p>
                      <p className="text-sm font-mono">{selectedCustomer.nif}</p>
                    </div>
                  )}

                  <div className="border-t pt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Gift className="w-5 h-5 text-green-600" />
                      <p className="font-medium">Cashback</p>
                    </div>
                    <p className="text-2xl font-bold text-green-600 mb-2">
                      {selectedCustomer.cashback_balance?.toFixed(2) || 0}€
                    </p>
                    {selectedCustomer.cashback_expires_at && (
                      <p className="text-xs text-gray-500 mb-3">
                        Caduca: {new Date(selectedCustomer.cashback_expires_at).toLocaleDateString()}
                      </p>
                    )}
                    
                    <div className="space-y-2">
                      <Input
                        type="number"
                        step="0.01"
                        value={cashbackAmount}
                        onChange={(e) => setCashbackAmount(e.target.value)}
                        placeholder="Nuevo balance"
                      />
                      <Button 
                        onClick={handleUpdateCashback}
                        className="w-full bg-black text-white"
                      >
                        Actualizar Cashback
                      </Button>
                    </div>
                  </div>

                  {/* Reset password */}
                  <div className="border-t pt-4" data-testid="admin-reset-password-section">
                    <p className="font-medium mb-2">Contraseña</p>
                    <p className="text-xs text-gray-600 mb-3">
                      Asigna una contraseña temporal al cliente (p.ej. si no puede iniciar sesión y no ha recibido el email de recuperación).
                    </p>
                    <Button
                      variant="outline"
                      className="w-full border-black hover:bg-gray-100"
                      data-testid="admin-reset-password-btn"
                      onClick={async () => {
                        const pwd = window.prompt(`Introduce una nueva contraseña para ${selectedCustomer.email} (mínimo 6 caracteres):`);
                        if (!pwd) return;
                        if (pwd.length < 6) {
                          toast.error('Debe tener al menos 6 caracteres');
                          return;
                        }
                        try {
                          const token = localStorage.getItem('token');
                          const res = await fetch(`${BACKEND_URL}/api/admin/customers/${selectedCustomer.id}/reset-password`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              Authorization: `Bearer ${token}`,
                            },
                            body: JSON.stringify({ new_password: pwd }),
                          });
                          if (res.ok) {
                            const data = await res.json().catch(() => ({}));
                            toast.success('Contraseña actualizada');
                            if (data.email_sent === true) {
                              toast.success('Email con nueva contraseña enviado al cliente');
                            } else if (data.email_sent === false) {
                              toast.error(`Email NO enviado: ${data.email_error || 'error desconocido'}. Comunica la contraseña manualmente.`, { duration: 10000 });
                            }
                          } else {
                            const err = await res.json().catch(() => ({}));
                            toast.error(err.detail || 'Error al actualizar');
                          }
                        } catch {
                          toast.error('Error de conexión');
                        }
                      }}
                    >
                      Resetear contraseña
                    </Button>
                  </div>

                  {/* ArtFitz Club Loyalty (replaces old VIP system) */}
                  <div className="border-t pt-4" data-testid="artfitz-club-info-section">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xl">⭐</span>
                      <p className="font-medium">ArtFitz Club</p>
                    </div>
                    {(() => {
                      const tier = selectedCustomer.loyalty_tier || 'bronce';
                      const points = Number(selectedCustomer.fitz_points || 0);
                      const labels = {
                        bronce: { name: 'Bronce', perk: '3% cashback', badge: 'bg-amber-100 text-amber-900 border-amber-400' },
                        plata:  { name: 'Plata',  perk: '5% descuento', badge: 'bg-gray-200 text-gray-800 border-gray-400' },
                        oro:    { name: 'Oro',    perk: '10% descuento', badge: 'bg-yellow-100 text-yellow-900 border-yellow-500' },
                        platino:{ name: 'Platino',perk: '20% descuento', badge: 'bg-slate-200 text-slate-900 border-slate-500' },
                        pro:    { name: 'Pro',    perk: '30% descuento', badge: 'bg-black text-white border-black' },
                      };
                      const cfg = labels[tier] || labels.bronce;
                      const cycleYear = selectedCustomer.cycle_year;
                      return (
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <span className={`inline-block px-3 py-1 rounded text-xs font-bold border ${cfg.badge}`} data-testid="customer-tier-badge">
                              {cfg.name.toUpperCase()}
                            </span>
                            <span className="text-sm text-gray-700">{cfg.perk}</span>
                            {selectedCustomer.loyalty_manual_override && (
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">Manual</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            Fitz Points ciclo actual: <strong data-testid="customer-fitz-points">{points.toFixed(2)}</strong>
                          </p>
                          {cycleYear && (
                            <p className="text-xs text-gray-500">
                              Ciclo: 1 Abr {cycleYear} – 31 Mar {cycleYear + 1}
                              {' · '}{selectedCustomer.cycle_orders_count || 0} pedidos
                              {' · '}{(selectedCustomer.cycle_orders_total || 0).toFixed(2)}€
                            </p>
                          )}
                          {selectedCustomer.loyalty_tier_expires_at && (
                            <p className="text-xs text-gray-500">
                              Caducidad nivel: {new Date(selectedCustomer.loyalty_tier_expires_at).toLocaleDateString('es-ES')}
                            </p>
                          )}
                          <Link
                            to="/admin/loyalty"
                            className="inline-block mt-2 text-sm text-black underline hover:no-underline"
                            data-testid="go-to-artfitz-club-link"
                          >
                            → Editar puntos, nivel o caducidad en ArtFitz Club
                          </Link>
                          <p className="text-xs text-gray-400 mt-2">
                            Vista sincronizada con el dashboard ArtFitz Club (misma fuente de datos).
                          </p>
                        </div>
                      );
                    })()}
                  </div>

                  <div className="border-t pt-4">
                    <p className="font-medium mb-3">Pedidos ({selectedCustomer.orders?.length || 0})</p>
                    {selectedCustomer.orders?.length > 0 ? (
                      <div className="space-y-2">
                        {selectedCustomer.orders.map((order) => (
                          <div key={order.id} className="text-sm border p-2 rounded">
                            <p className="font-medium">{order.total.toFixed(2)}€</p>
                            <p className="text-gray-600 text-xs">{new Date(order.created_at).toLocaleDateString()}</p>
                            <p className="text-xs">{order.order_status}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Sin pedidos</p>
                    )}
                  </div>
                </div>
                )}
              </div>
            ) : (
              <div className="bg-white p-6 border-2 border-black text-center text-gray-500">
                <Users className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                <p>Selecciona un cliente para ver los detalles</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Create Customer Form Component
function CreateCustomerForm({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'España',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password && formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/customers/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        toast.success('Cliente creado');
        // Report the credentials email outcome only if a password was set
        if (data.email_sent === true) {
          toast.success('Email de bienvenida con credenciales enviado');
        } else if (data.email_sent === false) {
          toast.error(`Email NO enviado: ${data.email_error || 'error desconocido'}`, { duration: 8000 });
        }
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al crear cliente');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6">
      <div className="bg-white p-8 border-2 border-black max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold">Crear Nuevo Cliente</h3>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Nombre *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Teléfono</Label>
              <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
            </div>
            <div>
              <Label>País</Label>
              <select className="w-full border-2 border-gray-300 p-2 rounded" value={formData.country} onChange={(e) => setFormData({...formData, country: e.target.value})}>
                <option value="España">España</option>
                <option value="Francia">Francia</option>
                <option value="Portugal">Portugal</option>
              </select>
            </div>
          </div>

          <div>
            <Label>Dirección</Label>
            <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Ciudad</Label>
              <Input value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} />
            </div>
            <div>
              <Label>Código Postal</Label>
              <Input value={formData.postal_code} onChange={(e) => setFormData({...formData, postal_code: e.target.value})} />
            </div>
          </div>

          <div>
            <Label>Contraseña (opcional)</Label>
            <Input
              type="text"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              placeholder="Mínimo 6 caracteres. Se enviarán las credenciales al cliente por email."
              data-testid="new-customer-password-input"
            />
          </div>

          <div className="bg-blue-50 p-4 rounded border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>Nota:</strong> Si asignas una contraseña, el cliente recibirá un email con sus credenciales de acceso. Si la dejas vacía, podrá acceder con Google Login usando este email o le puedes asignar pedidos manualmente.
            </p>
          </div>

          <div className="flex gap-3">
            <Button type="submit" className="bg-black text-white">Crear Cliente</Button>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
